/**
 * Shared Analytics Configuration
 *
 * Platform-agnostic analytics configuration shared between
 * analytics.native.ts and analytics.web.ts implementations.
 *
 * IMPORTANT: This module must NEVER import platform-specific SDKs
 * (@sentry/browser, @sentry/react-native) to avoid web/native coupling bugs.
 *
 * Refactored: 2026-01-09 (Fix Circular Import Bug)
 * - Renamed from index.ts to config.ts to break circular import ambiguity
 * - Removed all Sentry SDK imports (moved to platform-specific files)
 * - Removed getTraceHeaders() (moved to platform-specific files)
 * - Context management moved to platform-specific context.web.ts/context.native.ts
 */

import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { getSentryEnvironment } from "../environment";
import { logger } from "../logger";
import pkg from "../../package.json";

// ============================================================================
// Re-exports (Public API)
// ============================================================================

// Types
export type {
  AnalysisContext,
  DeviceContext,
  SessionContext,
  BreadcrumbCategory,
  BreadcrumbLevel,
} from './types';

// PII Scrubbing (Platform-agnostic)
export {
  PII_PATTERNS,
  scrubString,
  scrubObject,
  scrubPII,
  scrubBreadcrumb,
  scrubErrorForPostHog,
} from './scrubbers';

// NOTE: Context management functions are NO LONGER exported from here.
// They are now platform-specific and exported from:
// - analytics/context.web.ts (for web builds)
// - analytics/context.native.ts (for native builds)

// ============================================================================
// Environment Configuration (Shared)
// ============================================================================

/**
 * Get platform-specific Sentry DSN for 3-project architecture
 *
 * Enables separate Sentry projects per platform for better issue isolation:
 * - drama-web: Browser errors, Web Vitals, session replay
 * - drama-mobile: Native crashes, ANRs, mobile-specific spans
 * - drama-edge: Edge Function errors (uses SENTRY_DSN_EDGE separately)
 *
 * Distributed tracing works across all 3 projects via sentry-trace header
 * (trace_id is organization-scoped, not project-scoped)
 *
 * @see OBSERVABILITY_HUB.md for architecture details
 */
const getSentryDsn = (): string | undefined => {
  if (Platform.OS === 'web') {
    return process.env.EXPO_PUBLIC_SENTRY_DSN;
  }
  // iOS and Android use drama-mobile project
  // Falls back to legacy EXPO_PUBLIC_SENTRY_DSN for backwards compatibility
  return process.env.EXPO_PUBLIC_SENTRY_DSN_MOBILE || process.env.EXPO_PUBLIC_SENTRY_DSN;
};

export const SENTRY_DSN = getSentryDsn();
export const POSTHOG_KEY = process.env.EXPO_PUBLIC_POSTHOG_KEY;
export const POSTHOG_HOST = process.env.EXPO_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
export const ANALYTICS_ENABLED = process.env.EXPO_PUBLIC_ENABLE_ANALYTICS !== "false";
export const APP_VERSION = pkg.version;
export const SENTRY_ENVIRONMENT = getSentryEnvironment();

/**
 * Get commit SHA for Sentry release naming (zero-maintenance automation)
 *
 * Reads from Constants.expoConfig.extra.commitSha, which is set at build time
 * by app.config.js. This bridges build-time env vars (VERCEL_GIT_COMMIT_SHA,
 * EAS_BUILD_GIT_COMMIT_HASH) to runtime, since Metro only bundles EXPO_PUBLIC_* vars.
 *
 * Falls back through manifest2/manifest for EAS Update and older Expo runtimes
 * where expoConfig may be null.
 *
 * @returns Full commit SHA or 'local' for development
 */
const getCommitSha = (): string => {
  const extra = Constants.expoConfig?.extra
    ?? (Constants.manifest2 as any)?.extra?.expoClient?.extra
    ?? (Constants.manifest as any)?.extra;
  return extra?.commitSha || 'local';
};

export const COMMIT_SHA = getCommitSha();

/**
 * Get Sentry release name for error tracking and source map correlation.
 *
 * Format: drama-detector-{shortSha} (e.g., drama-detector-a1b2c3d)
 * Development: drama-detector-local
 *
 * Unified format used by both web CI (sentry:release script) and mobile EAS builds
 * so that cross-platform issue correlation and source map uploads work correctly.
 *
 * Reads from Constants.expoConfig.extra.sentryRelease, which is computed at
 * build time by app.config.js using the commit SHA.
 *
 * Falls back to SENTRY_RELEASE env var for CI overrides,
 * then to runtime computation if unavailable.
 *
 * @see apps/mobile/package.json#sentry:release (web CI script)
 * @see apps/mobile/app.config.js (mobile EAS build)
 * @see docs/SENTRY_RELEASE_FORMAT_SPEC.md
 */
export const getSentryRelease = (): string => {
  // Priority 1: Explicit CI/CD override (e.g., semantic-release or manual override)
  const cicdRelease = process.env.SENTRY_RELEASE;
  if (cicdRelease && cicdRelease.startsWith('drama-detector')) {
    return cicdRelease.trim();
  }

  // Priority 2: Build-time release from app.config.js (drama-detector-{shortSha})
  const extra = Constants.expoConfig?.extra
    ?? (Constants.manifest2 as any)?.extra?.expoClient?.extra
    ?? (Constants.manifest as any)?.extra;
  const buildTimeRelease = extra?.sentryRelease;
  if (buildTimeRelease && buildTimeRelease.startsWith('drama-detector')) {
    return buildTimeRelease;
  }

  // Priority 3: Fallback for development builds
  // Call getCommitSha() fresh to read current Constants (not frozen module-level value)
  const commitSha = getCommitSha();
  if (commitSha && commitSha !== 'local') {
    return `drama-detector-${commitSha.substring(0, 7)}`;
  }
  return 'drama-detector-local';
};

export const SENTRY_RELEASE = getSentryRelease();

// ============================================================================
// Sentry Initialization (Shared Configuration)
// ============================================================================

/**
 * Get shared Sentry configuration
 *
 * Phase 1 Enhancements:
 * - Environment separation (disabled in development)
 * - Release tracking for deployment correlation
 * - Build dist tracking
 * - Development error filtering
 *
 * Phase 6 Enhancements (Release Management):
 * - Semantic versioning support for releases
 * - Commit SHA fallback for non-release builds
 * - Alignment with Sentry CLI finalization script
 */
export function getSharedSentryConfig() {
  return {
    dsn: SENTRY_DSN,
    enabled: !__DEV__, // Phase 1: Never track dev errors (migrated from enableInExpoDevelopment)
    debug: __DEV__,
    tracesSampleRate: SENTRY_ENVIRONMENT === "production" ? 0.2 : 1.0, // 20% prod, 100% staging
    enableAutoSessionTracking: true, // Phase 5: Release health

    // Release tracking with semantic versioning support
    // Format: 'drama-detector@1.2.3' (releases) or 'drama-detector@local-abc123' (dev)
    // Matches format used by Sentry CLI finalization script
    release: SENTRY_RELEASE,

    // dist = full commit SHA for precise source map correlation
    dist: COMMIT_SHA,
    environment: SENTRY_ENVIRONMENT,
  };
}

/**
 * Shared Sentry beforeSend logic
 */
export function shouldDropSentryEvent(event: any): boolean {
  // Phase 1: Filter out development errors (double-check)
  if (event.environment === "development") {
    logger.log("[Analytics] Dropping development error");
    return true;
  }
  return false;
}

// ============================================================================
// Analytics Validation (Shared Guards)
// ============================================================================

export function shouldInitializeSentry(): boolean {
  if (!SENTRY_DSN || !ANALYTICS_ENABLED) {
    logger.log("[Analytics] Sentry disabled (no DSN or analytics disabled)");
    return false;
  }

  // Phase 1: Disable in development to avoid noise
  if (__DEV__ && SENTRY_ENVIRONMENT === "development") {
    logger.log("[Analytics] Sentry disabled in development environment");
    return false;
  }

  return true;
}

export function shouldInitializePostHog(): boolean {
  if (!POSTHOG_KEY || !ANALYTICS_ENABLED) {
    logger.log("[Analytics] PostHog disabled (no key or analytics disabled)");
    return false;
  }

  return true;
}

// ============================================================================
// Distributed Tracing (Cross-Service Correlation)
// ============================================================================

// NOTE: getTraceHeaders() has been REMOVED from this shared config file.
// It is now implemented in platform-specific files because:
// 1. It requires Sentry SDK access (different APIs for web vs native)
// 2. Web needs @sentry/browser, native needs @sentry/react-native
//
// See implementations in:
// - analytics.web.ts (uses @sentry/browser API)
// - analytics.native.ts (uses @sentry/react-native API)
